import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FaPlus, FaEdit, FaMotorcycle, FaCar, FaTimes } from 'react-icons/fa';
import Alert from '../common/Alert';
import FormInput from '../common/FormInput';
import Button from '../common/Button';
import IconButton from '../common/IconButton';
import useApi from '../../hooks/useApi';
import { toast } from 'react-toastify';
import 'animate.css';

/**
 * Vehicle form for adding/editing vehicles
 * @param {Object} props
 * @param {Object} [props.vehicle] - Vehicle to edit (null for add mode)
 * @param {Function} props.onSuccess - Callback on success
 * @param {Function} props.onCancel - Callback on cancel
 */
function VehicleForm({ vehicle = null, onSuccess = () => {}, onCancel = () => {} }) {
  const [formData, setFormData] = useState({
    vehicleNo: vehicle?.vehicleNo || '',
    type: vehicle?.type || '',
  });
  const [error, setError] = useState('');
  const [vehicleNoError, setVehicleNoError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const rcFile = useRef(null);
  const { post, put } = useApi();

  // Sync form data with vehicle prop changes
  useEffect(() => {
    setFormData({
      vehicleNo: vehicle?.vehicleNo || '',
      type: vehicle?.type || '',
    });
    setError('');
    setVehicleNoError('');
  }, [vehicle]);

  const validateVehicleNo = (vehicleNo) => {
    if (!vehicleNo) {
      return { isValid: false, errorMessage: 'Vehicle number is required.' };
    }
    const normalized = vehicleNo.replace(/[\s-]/g, '').toUpperCase();
    const standardPattern = /^[A-Z]{2}\d{1,2}[A-Z]{1,3}\d{1,4}$/;
    const bharatPattern = /^\d{2}BH\d{4}[A-Z]{1,2}$/;
    if (!standardPattern.test(normalized) && !bharatPattern.test(normalized)) {
      return {
        isValid: false,
        errorMessage: 'Enter a valid registration, for example MH12AB1234 or 22BH1234AA.',
      };
    }
    return { isValid: true, errorMessage: '' };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'vehicleNo' ? value.toUpperCase() : value;
    setFormData({ ...formData, [name]: nextValue });
    if (name === 'vehicleNo') {
      const { isValid, errorMessage } = validateVehicleNo(nextValue);
      setVehicleNoError(isValid ? '' : errorMessage);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { isValid, errorMessage } = validateVehicleNo(formData.vehicleNo);
    if (!isValid) {
      setVehicleNoError(errorMessage);
      return;
    }
    try {
      setIsSubmitting(true);
      setError('');
      const payload = { ...formData, vehicleNo: formData.vehicleNo.replace(/[\s-]/g, '').toUpperCase() };
      const response = vehicle
        ? await put(`/api/vehicles/${vehicle.id}`, payload)
        : await post('/api/vehicles', payload);
      if (!vehicle && rcFile.current?.files?.[0]) {
        const documentBody = new FormData();
        documentBody.append('file', rcFile.current.files[0]);
        try {
          await post(`/api/vehicle-documents/vehicle/${response.id}`, documentBody);
        } catch (documentError) {
          onSuccess(response);
          toast.warning(`Vehicle saved, but the RC document could not be uploaded: ${documentError.message}`);
          return;
        }
      }
      onSuccess(response);
      if (!vehicle) {
        setFormData({ vehicleNo: '', type: '' });
        setError('');
        setVehicleNoError('');
      }
      toast.success(vehicle ? 'Vehicle updated.' : 'Vehicle added.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vehicle-form-panel animate__animated animate__fadeIn">
      <div className="card-body">
        <div className="vehicle-panel-kicker">{vehicle ? 'Edit registration' : 'New registration'}</div>
        <h5 className="vehicle-panel-title">{vehicle ? 'Update vehicle' : 'Add a vehicle'}</h5>
        <p className="vehicle-panel-copy">Keep your community parking and gate records up to date.</p>
        {error && <Alert type="danger" message={error} />}
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <FormInput
              label="Vehicle Number"
              name="vehicleNo"
              value={formData.vehicleNo}
              onChange={handleChange}
              required
              placeholder="MH23BE4467"
            />
            {vehicleNoError && <Alert type="danger" message={vehicleNoError} />}
          </div>
          <div className="mb-3">
            <label className="form-label">Vehicle Type</label>
            <div className="d-flex justify-content-around">
              <div className="form-check vehicle-type-radio">
                <input
                  type="radio"
                  className="form-check-input"
                  id={`type-two-wheeler-${vehicle?.id || 'new'}`}
                  name="type"
                  value="two-wheeler"
                  checked={formData.type === 'two-wheeler'}
                  onChange={handleChange}
                  required
                />
                <label className="form-check-label d-flex align-items-center" htmlFor={`type-two-wheeler-${vehicle?.id || 'new'}`}>
                  <FaMotorcycle size={30} color="#007bff" className="me-2" />
                  Two-Wheeler
                </label>
              </div>
              <div className="form-check vehicle-type-radio">
                <input
                  type="radio"
                  className="form-check-input"
                  id={`type-four-wheeler-${vehicle?.id || 'new'}`}
                  name="type"
                  value="four-wheeler"
                  checked={formData.type === 'four-wheeler'}
                  onChange={handleChange}
                  required
                />
                <label className="form-check-label d-flex align-items-center" htmlFor={`type-four-wheeler-${vehicle?.id || 'new'}`}>
                  <FaCar size={30} color="#28a745" className="me-2" />
                  Four-Wheeler
                </label>
              </div>
            </div>
          </div>
          {!vehicle && <div className="mb-3"><label className="form-label">RC book <span className="text-muted">(optional)</span><input ref={rcFile} type="file" accept=".pdf,.jpg,.jpeg,.png" className="form-control mt-1"/><small className="text-muted">Visible only to you and community administrators for verification. Maximum 5 MB.</small></label></div>}
          <div className="d-flex gap-2">
            <Button
              type="submit"
              variant="primary"
              className="w-50"
            disabled={!!vehicleNoError || isSubmitting}
            >
              {vehicle ? <FaEdit /> : <FaPlus />} {isSubmitting ? 'Saving…' : vehicle ? 'Update' : 'Add vehicle'}
            </Button>
            <IconButton
              icon={<FaTimes />}
              tooltip="Cancel"
              variant="secondary"
              onClick={() => {
                setFormData({ vehicleNo: '', type: '' });
                setVehicleNoError('');
                onCancel();
              }}
              className="w-50"
            />
          </div>
        </form>
      </div>
    </div>
  );
}

VehicleForm.propTypes = {
  vehicle: PropTypes.shape({
    id: PropTypes.number,
    vehicleNo: PropTypes.string,
    type: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
};

export default VehicleForm;
